import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {Affix, BackTop, Layout, Segmented, Tabs} from 'antd';
import LoadingBanner from "../../components/LoadingBanner";
import useOverscroll from "../../hooks/useOverscroll";

const ProfileLayoutWrapper = ({ type, sections, isLoading, children }) => {
    const location = useLocation();
    const [activeSection, setActiveSection] = useState(null);
    const sectionRefs = useRef([]);
    const groupedSectionRefs = useRef([]);
    const [isProfileNavAffixed, setIsProfileNavAffixed] = useState(false)
    const [sectionsContainer, setSectionsContainer] = useState(null);


    const navSectionCategories = {
        Match: ['Match Score'],
        Members: ['Team Members'],
        Work: ['Brand Experience', 'Work Highlights', 'Wripple Projects'],
        Expertise: ['Skills', 'AI Experience', 'Industry Experience', 'Teams'],
        Resume: ['Work History','Education', 'Awards', 'Certifications'],
        'Pre-Screen':['References', 'Interviews'],
        'Job Status': ['Work Status', 'Preferences'],
    };

    const groupsCategories = useMemo(() => {
        return TB.reduce(navSectionCategories,(result, value, key) => {
            let section = {};
            TB.map(value, (sectionKey) => {
                let profileSection = TB.find(sections,['title', sectionKey])
                if (profileSection) {
                    section[sectionKey] = profileSection;
                }
            })
            if (Object.keys(section)?.length) {
                result[key] = section;
            }
            return result;
        }, {});
    }, [sections]);


    const scrollToSection = useCallback((elementId, offset = 120) => {
        const element = document.getElementById(elementId);
        if (element) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    }, []);

    useEffect(() => {
        if (!isLoading && location.hash) {
            const section = document.getElementById(location.hash.substring(1))
            section && scrollToSection(section.id)
        }
    }, [isLoading])

    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash;
            if (hash) {
                // Remove the # from the hash to get the element ID
                const elementId = hash.replace('#', '');
                // Add a small delay to ensure the DOM is ready
                setTimeout(() => scrollToSection(elementId), 0);
            }
        };

        // Handle hash on initial page load
        if (window.location.hash) {
            handleHashChange();
        }

        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [scrollToSection]);


    const handleScroll = () => {
        const pageYOffset = window.pageYOffset + 200;
        let newActiveSection = null;

        sectionRefs.current.forEach((section) => {
            if (section) {
                const sectionOffsetTop = section.offsetTop;
                const sectionHeight = section.offsetHeight;

                if (pageYOffset >= sectionOffsetTop && pageYOffset < sectionOffsetTop + sectionHeight) {
                    // newActiveSection = section.id;

                    // find section from groupsCategories to match casing
                    TB.map(groupsCategories, (childSections, groupKey) => {
                        const matchingSection = TB.find(childSections, child => section.id == TB.slugify(child.title));
                        if (matchingSection) {
                            newActiveSection = matchingSection.title
                        }
                    })
                }
            }
        });

        setActiveSection(newActiveSection);
    };

    useEffect(() => {
        if (!sections.length) return;
        sectionRefs.current = sections.map(({ title }) => document.getElementById(TB.slugify(title)));
        groupedSectionRefs.current = TB.map(groupsCategories, (childSections, groupKey) => {

            return document.getElementById(TB.slugify(TB.first(TB.values(childSections)).title))
        });
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [sections]);

    const activeGroupSection = useMemo(() => {
        let activeGroup = null;

        if (!activeSection) {
            return TB.first(TB.keys(groupsCategories))
        }
        TB.map(groupsCategories, (childSections, groupKey) => {
            // if (TB.find(childSections, child => activeSection === TB.slugify(child.title))) {
            if (TB.find(childSections, child => activeSection === child.title)) {
                activeGroup = groupKey;
            }
        })
        return activeGroup;
    }, [groupsCategories, activeSection]);

    const [isProfileDetailsAffixed, setIsProfileDetailsAffixed] = useState(false);
    const [isProfileDetailsVisible, setIsProfileDetailsVisible] = useState(true);

    // Listen for overscrolling
    const { isOverscrolling } = useOverscroll({
        threshold: -5,
        onOverscroll: (event) => {
            console.log('Overscrolling!', event);
        },
        onReset: (event) => {
            console.log('Back to normal: Reset affixed', event);

            setIsProfileDetailsVisible(false);
        }
    });


    if (isLoading) {
        return (
            <div className="w-full flex-1 h-full flex flex-row items-center justify-center">
                <LoadingBanner message={`Loading ${type}`} />
            </div>
        )
    }

    const shouldMinimizeContent = isProfileDetailsVisible && isProfileDetailsAffixed

    return (
        <Layout  className="relative talent-profile-redesign bg-wripplePrimaryRuleBlue">

            <div ref={setSectionsContainer} className="bg-wripplePrimaryRuleBlue pt-6 flex flex-col md:flex-row flex-nowrap gap-6 mx-auto w-full lg:max-w-[1368px] px-2">
                <div className="hidden md:block">
                    <Affix target={isProfileDetailsVisible ? undefined : null} onChange={async (isAffixed) => {
                        const wasAffixed = isProfileDetailsAffixed;
                        await setIsProfileDetailsAffixed(isAffixed);
                        if (wasAffixed && !isAffixed && isProfileDetailsVisible) {
                            console.log('reset', { wasAffixed, isProfileDetailsVisible })
                            await setIsProfileDetailsVisible(false);
                        }
                    }}
                    >
                        {children && React.Children.map(children, child => (
                            React.cloneElement(children, {
                                shouldMinimizeContent,
                                isVisible: isProfileDetailsVisible,
                                onVisible: async (visible) => {
                                    console.log('visible!!!', visible)
                                    await setIsProfileDetailsVisible(visible);
                                },
                                ...child.props
                            })
                        ))}
                    </Affix>
                </div>

                <div className="md:hidden">
                    {children && React.Children.map(children, child => (
                        React.cloneElement(children, {
                            shouldMinimizeContent,
                            isVisible: isProfileDetailsVisible,
                            onVisible: async (visible) => {
                                console.log('visible!!!', visible)
                                await setIsProfileDetailsVisible(visible);
                            },
                            ...child.props
                        })
                    ))}
                </div>


                <div className="flex-grow px-2">

                    {(type === 'Agency' || type === 'Team') ? (
                        <React.Fragment>
                            <div className="flex flex-col md:flex-row gap-4 py-2 -mt-2 px-1 -mx-1 bg-wripplePrimaryRuleBlue md:sticky top-0 z-50">
                                <div>
                                    <p className="font-bold py-2 whitespace-nowrap text-sm">Quick Links:</p>
                                    <BackTop duration={200} onClick={() => window.history.replaceState("", document.title, window.location.pathname + window.location.search)} />
                                </div>

                                <div className="flex gap-2 flex-wrap">
                                    {sections.map(({ title }) => {
                                        return (
                                            <a key={TB.slugify(title)} href={`#${TB.slugify(title)}`}
                                               className={TB.mapClass('text-sm bg-wrippleOffWhite py-2 px-3 hover:text-secondary', {
                                                   'text-wripplePrimaryBrightPurple': activeSection === TB.slugify(title),
                                               })}>
                                                {title}
                                            </a>
                                        )
                                    })}
                                </div>
                            </div>

                            <div id="" className="w-full flex flex-col pb-20 pt-6">
                                {sections?.map(({ title, element: ProfileItemSection, ...sectionProps }) => {
                                    return (
                                        <ProfileItemSection key={`section-${TB.slugify(title)}`} {...sectionProps} />
                                    )
                                })}
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <Affix onChange={setIsProfileNavAffixed}>
                                <div className={TB.mapClass("drop-shadow-sm flex flex-col bg-wripplePrimaryRuleBlue ", {
                                    "drop-shadow-md": isProfileNavAffixed
                                })}>
                                    <div className="profile-main-nav">
                                        <Tabs items={TB.map(Object.keys(groupsCategories), group => {
                                            return {
                                                label: group,
                                                key: group,
                                            }
                                        })}
                                              activeKey={activeGroupSection}

                                              defaultActiveKey={TB.first(TB.keys(groupsCategories))}
                                              onChange={(group) => {
                                                  scrollToSection(TB.slugify(TB.first(TB.values(groupsCategories[group])).title))
                                              }}/>

                                    </div>
                                    {activeGroupSection && (
                                        <div className="profile-sub-nav [&_.ant-segmented-item]:flex-none">
                                            <Segmented
                                                options={Object.keys(groupsCategories[activeGroupSection])}
                                                // value={TB.titleCase(activeSection) || TB.first(TB.keys(groupsCategories[activeGroupSection]))}
                                                value={activeSection || TB.first(TB.keys(groupsCategories[activeGroupSection]))}
                                                defaultValue={TB.first(TB.keys(groupsCategories[activeGroupSection]))}
                                                block={true}
                                                size="large"
                                                onChange={(childGroup) => {
                                                    scrollToSection(TB.slugify(childGroup))
                                                }}
                                            />
                                        </div>

                                    )}
                                </div>
                            </Affix>

                            <div  className="relative w-full flex flex-col pb-20 pt-6">
                                <BackTop duration={200} onClick={() => window.history.replaceState("", document.title, window.location.pathname + window.location.search)} />

                                {TB.map(groupsCategories, (childSections, groupKey) => {
                                    return (
                                        <React.Fragment key={groupKey}>
                                            {
                                                TB.map(childSections,({ title, element: ProfileItemSection, ...sectionProps }) => {
                                                    return (
                                                        <ProfileItemSection key={`section-${TB.slugify(title)}`} {...sectionProps} />
                                                    )
                                                })
                                            }
                                        </React.Fragment>
                                    )
                                })}
                            </div>
                        </React.Fragment>
                    )}


                </div>
            </div>
        </Layout>

    )
}

export default ProfileLayoutWrapper;